import { assertUnreachable } from '@sgme/fp';
import { BlotterGrid } from 'components/shared/Blotter/Grid';
import type { BlotterGridProps } from 'components/shared/Blotter/Grid/blotter.model';
import { canSetActive } from 'components/shared/Blotter/utils';
import type { IntlKey } from 'locales';
import type { IBlotterItem } from 'models/IBlotterItem';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { connectSecondaryMarketBlotterGrid } from './Grid.connect';
import type { Market, SecondaryTradeBlotterItem, SecondaryTradeSide, SecondaryTradeStatus } from './Model';

const getTranslationKeyForTradeSide: (side: SecondaryTradeSide) => IntlKey = side => {
  switch (side) {
    case 'Client Buys':
      return 'secondaryMarketBlotter.column.header.tradeSide.ClientBuys';
    case 'Client Sells':
      return 'secondaryMarketBlotter.column.header.tradeSide.ClientSells';
    case 'Unknown':
      return 'secondaryMarketBlotter.column.header.tradeSide.Unkwnown';
    default:
      return assertUnreachable(side, 'Unhandled side');
  }
};

export const getTranslationKeyForTradeStatus: (status: SecondaryTradeStatus) => IntlKey = status => {
  switch (status) {
    case 'Successfull':
    case 'Traded':
    case 'Rejected':
    case 'Cancelled':
    case 'Expired':
    case 'Pending':
    case 'Unknown':
    case 'Timeout':
      return `secondaryMarketBlotter.column.header.status.${status}`;
    default:
      return assertUnreachable(status, 'Unhandled trade status');
  }
};

const getTranslationKeyForMarket: (market: Market) => IntlKey = market => {
  switch (market) {
    case 'Secondary':
      return 'secondaryMarketBlotter.column.header.market.secondary';
    default:
      return assertUnreachable(market, 'Unhandled market');
  }
};

export const Grid: FC<BlotterGridProps<IBlotterItem>> = ({ items, ...props }) => {
  const { formatMessage } = useIntl();

  const localize = useCallback(
    (key?: IntlKey) => (key !== undefined ? formatMessage({ id: key, defaultMessage: key }) : undefined),
    [formatMessage],
  );

  const translatedItems = useMemo(() => {
    return items?.map(({ tradeSide, status, market, ...nonTranslated }: SecondaryTradeBlotterItem) => {
      return {
        ...nonTranslated,
        tradeSide: localize(getTranslationKeyForTradeSide(tradeSide)),
        status: localize(getTranslationKeyForTradeStatus(status)),
        market: localize(getTranslationKeyForMarket(market)),
      };
    });
  }, [items, localize]);
  return <BlotterGrid {...props} items={translatedItems} canSetActive={canSetActive} />;
};

export const SecondaryMarketBlotterGrid = connectSecondaryMarketBlotterGrid(Grid);
